import { useQueries } from "@tanstack/react-query";
import { useBaseQuery } from "./use-base-query";
import { application } from "../application/application";
import { getList, getModule } from "../features/common/graphql/queries";
import { parseGraphQLResponse } from "../utils/apiMaper";
import { INavigationConfig } from "../features/administration/partials/navigation-menu-section";
import { MICRO_APP_LOGIC_LAYER, LOGIC_LAYER_NAV_MODULE } from "../config/constants";
import { queryClient } from "../application/query-client";

export const prefetchNavigationConfigs = async (): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: ["navigationList"],
    queryFn: async () => {
      const response = await application.network.fetch(
        getList,
        { module: LOGIC_LAYER_NAV_MODULE, keyAccountId: "" },
        MICRO_APP_LOGIC_LAYER,
      );
      return parseGraphQLResponse(response as any, false) as any[];
    },
    staleTime: Infinity,
  });

  const navList = queryClient.getQueryData<any[]>(["navigationList"]) || [];
  await Promise.all(
    navList.filter((item) => item.id).map((item) =>
      queryClient.prefetchQuery({
        queryKey: ["navigationConfig", item.id],
        queryFn: async () => {
          const response = await application.network.fetch(
            getModule,
            { module: LOGIC_LAYER_NAV_MODULE, keyAccountId: "", idx: item.id },
            MICRO_APP_LOGIC_LAYER,
          );
          return parseGraphQLResponse(response as any, false) as unknown as INavigationConfig;
        },
        staleTime: Infinity,
        retry: false,
      }),
    ),
  );
};

export const useNavigationConfigs = () => {
  // Step 1: Fetch list of navigation IDs
  const listQuery = useBaseQuery<any[]>(
    ["navigationList"],
    getList,
    { module: LOGIC_LAYER_NAV_MODULE, keyAccountId: "" },
    { staleTime: Infinity, microapp: MICRO_APP_LOGIC_LAYER },
  );

  const navList = listQuery.data || [];

  // Step 2: Fetch each navigation config by ID
  const configQueries = useQueries({
    queries: navList.map((item: any) => ({
      queryKey: ["navigationConfig", item.id],
      queryFn: async () => {
        const response = await application.network.fetch(
          getModule,
          { module: LOGIC_LAYER_NAV_MODULE, keyAccountId: "", idx: item.id },
          MICRO_APP_LOGIC_LAYER,
        );
        return parseGraphQLResponse(response as any, false) as unknown as INavigationConfig;
      },
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!item.id,
    })),
  });

  const isLoading = listQuery.isLoading || configQueries.some((q) => q.isLoading);
  const error = listQuery.error || configQueries.find((q) => q.error)?.error || null;
  const navigationConfigs = configQueries
    .map((q) => q.data)
    .filter(Boolean) as INavigationConfig[];

  return {
    navigationConfigs,
    isLoading,
    error: error as Error | null,
  };
};
